import { Static, Type } from '@sinclair/typebox';
import { WebsiteColorsSchema, WebsiteStylesSchema } from './Styles';
import { WEBSITE_STYLE_PRESET_IDS, type WebsiteStylePresetId } from './style-presets';
import { WebsiteConfigSchema } from './WebsiteTypes';

/**
 * Onboarding v4, lot 7 : contrat des routes « style du site » (`GET app/pro-website/style`,
 * `POST .../style/apply`, `POST .../style/palette`, `POST .../style/logo/generate`) et des
 * colonnes `professional.brand_palette` / `logo_provisional`, `onboarding_state.website_choice`.
 */

/** D'où viennent les couleurs de marque du pro (colonne JSON `professional.brand_palette.source`). */
export enum BrandPaletteSource {
  /** Extraites du logo chargé par le pro. */
  LOGO = 'logo',
  /** Couleur lue sur le site existant du pro (détection). */
  SITE = 'site',
  /** Choisies à la main dans le sélecteur. */
  CUSTOM = 'custom',
  /** Aucune couleur propre : palette du secteur. */
  SECTOR = 'sector',
}

/** Choix du pro qui a déjà un site (plan §4.2) : passer sur Propelsia ou garder le sien. */
export enum WebsiteChoice {
  PROPELSIA = 'propelsia',
  OWN_SITE = 'own_site',
}

// `Type.Unsafe<T>` garde le type littéral côté `Static` (une union construite par `map` le perd).
const literalUnion = <T extends string>(values: readonly T[]): ReturnType<typeof Type.Unsafe<T>> =>
  Type.Unsafe<T>(Type.Union(values.map((value): ReturnType<typeof Type.Literal> => Type.Literal(value))));

export const WebsiteStylePresetIdSchema = literalUnion<WebsiteStylePresetId>(WEBSITE_STYLE_PRESET_IDS);
export const BrandPaletteSourceSchema = literalUnion<BrandPaletteSource>(Object.values(BrandPaletteSource));
export const WebsiteChoiceSchema = literalUnion<WebsiteChoice>(Object.values(WebsiteChoice));

/** Forme de la colonne JSON `professional.brand_palette`. */
export const BrandPaletteSchema = Type.Object({
  colors: WebsiteColorsSchema,
  source: BrandPaletteSourceSchema,
  /** Le contraste a dû être corrigé à l'extraction (on le dit au pro). */
  adjusted: Type.Optional(Type.Boolean()),
  extracted_at: Type.String(),
});
export type BrandPaletteObject = Static<typeof BrandPaletteSchema>;

export const WebsiteStylePresetSummarySchema = Type.Object({
  id: WebsiteStylePresetIdSchema,
  label_key: Type.String(),
  description_key: Type.String(),
  /** Clé `palette_*` retenue pour le secteur du pro. */
  palette_key: Type.String(),
  /** Couleurs du preset pour le secteur (avant couleurs de marque). */
  colors: WebsiteColorsSchema,
  styles: Type.Omit(WebsiteStylesSchema, ['colors']),
});

export const WebsiteStyleResponseSchema = Type.Object({
  /** Preset appliqué en dernier sur le brouillon, null sans preset. */
  template: Type.Union([WebsiteStylePresetIdSchema, Type.Null()]),
  sector: Type.String(),
  presets: Type.Array(WebsiteStylePresetSummarySchema),
  brand: Type.Union([BrandPaletteSchema, Type.Null()]),
  logo: Type.Union([Type.Object({ url: Type.String(), provisional: Type.Boolean() }), Type.Null()]),
  /** Clés S3 des photos du pro (galerie, héros) — la matière des mini-aperçus. */
  photo_keys: Type.Array(Type.String()),
  has_google_reviews: Type.Boolean(),
  has_travel_zone: Type.Boolean(),
  has_published_news: Type.Boolean(),
  is_published: Type.Boolean(),
});
export type WebsiteStyleResponseObject = Static<typeof WebsiteStyleResponseSchema>;

export const ApplyWebsiteStyleRequestSchema = Type.Object({
  preset_id: WebsiteStylePresetIdSchema,
  /** `brand` = couleurs de marque du pro si elles existent, `sector` = palette du preset. */
  colors: Type.Optional(Type.Union([Type.Literal('brand'), Type.Literal('sector')])),
  /** D'où vient l'application (événement `site_template_applied`). */
  via: Type.Optional(Type.Union([Type.Literal('tunnel'), Type.Literal('recap'), Type.Literal('editor')])),
});
export type ApplyWebsiteStyleRequestObject = Static<typeof ApplyWebsiteStyleRequestSchema>;

export const ApplyWebsiteStyleResponseSchema = Type.Object({
  config: WebsiteConfigSchema,
  template: WebsiteStylePresetIdSchema,
});
export type ApplyWebsiteStyleResponseObject = Static<typeof ApplyWebsiteStyleResponseSchema>;

export const SaveBrandPaletteRequestSchema = Type.Object({
  colors: WebsiteColorsSchema,
});
export type SaveBrandPaletteRequestObject = Static<typeof SaveBrandPaletteRequestSchema>;

export const GenerateLogoRequestSchema = Type.Object({
  /** Police du style choisi pour le monogramme (sinon celle du preset courant ou une sans-serif). */
  preset_id: Type.Optional(WebsiteStylePresetIdSchema),
});
export type GenerateLogoRequestObject = Static<typeof GenerateLogoRequestSchema>;

export const GenerateLogoResponseSchema = Type.Object({
  url: Type.String(),
  provisional: Type.Boolean(),
  brand: Type.Union([BrandPaletteSchema, Type.Null()]),
});
export type GenerateLogoResponseObject = Static<typeof GenerateLogoResponseSchema>;
